import { defineStore } from 'pinia';
import request from '@/utils/request';
import useUserStore from '@/store/user';
import { Toast } from '@/utils/toast';

export interface UserSubscribeInfo {
  is_vip: boolean;
  vip_end_time: number;
  vip_level: number;
  timestamp?: number;
}

const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    userSubscribeInfo: null as UserSubscribeInfo | null,
    _fetchPromise: null as Promise<UserSubscribeInfo | null> | null,
  }),
  getters: {
    isVip: state => state.userSubscribeInfo?.is_vip ?? false,
  },
  actions: {
    /**
     * Fetch subscription info from API. Only fetches once per session;
     * subsequent calls return cached data.
     */
    async ensureLoaded(): Promise<UserSubscribeInfo | null> {
      const userStore = useUserStore();
      if (!userStore.isLogin) {
        this.userSubscribeInfo = null;
        return null;
      }
      if (this.userSubscribeInfo !== null) return this.userSubscribeInfo;
      if (this._fetchPromise) {
        await this._fetchPromise;
        return this.userSubscribeInfo;
      }
      this._fetchPromise = this.fetchUserSubscribeInfo();
      try {
        const info = await this._fetchPromise;
        this.userSubscribeInfo = info;
        return info;
      } finally {
        this._fetchPromise = null;
      }
    },
    async fetchUserSubscribeInfo(): Promise<UserSubscribeInfo | null> {
      try {
        const res: any = await request.get(`/pay/c/vip/me_sign.json`, {
          headers: { v_p: '0.2.0' as any },
        });
        const dataStr = typeof res?.data === 'string' ? res.data : res?.data?.data;
        const parsed = dataStr ? JSON.parse(dataStr) : null;
        if (!parsed) return null;
        const info: UserSubscribeInfo = {
          is_vip: !!parsed.is_vip,
          vip_end_time: parsed.vip_end_time ?? 0,
          vip_level: parsed.vip_level ?? 0,
          timestamp: Date.now(),
        };
        return info;
      } catch {
        Toast('Failed to parse subscription info');
        return null;
      }
    },
    /**
     * Set subscription info (e.g. when another part of the app fetches it).
     */
    setUserSubscribeInfo(info: UserSubscribeInfo) {
      this.userSubscribeInfo = info;
    },
    /**
     * Clear subscription info (e.g. on logout).
     */
    clear() {
      this.userSubscribeInfo = null;
      this._fetchPromise = null;
    },
  },
});

export default useSubscriptionStore;
